/**
 * Shared attribute-pill row for both InlineChipCard (editor) and
 * SuggestionStrip (strip mode). Renders cortex `candidateAttributes`
 * grouped by dim, with the LLM probability per pill, and reports
 * toggle events through `onTogglePill`.
 *
 * Behavior-free presentational component — the parent owns state
 * (current selection, phrase rebuilding) so this can be reused
 * across both surfaces without dragging editor or strip state into
 * it.
 */

import type { CandidateAttribute } from "../../hooks/useReportQuery";

const DIM_LABELS: Record<string, string> = {
  laterality: "Laterality",
  size: "Size",
  severity: "Severity",
  acuity: "Acuity",
  obstruction: "Obstruction",
  calcification_status: "Calcification",
};

const DIM_ORDER = [
  "laterality",
  "size",
  "severity",
  "acuity",
  "obstruction",
  "calcification_status",
];

interface ChipAttributePillsProps {
  candidates: CandidateAttribute[];
  /** Dim → selected value (null = none selected for that dim). */
  selected: Record<string, string | null>;
  onTogglePill: (dim: string, value: string) => void;
}

export function ChipAttributePills({
  candidates,
  selected,
  onTogglePill,
}: ChipAttributePillsProps) {
  if (candidates.length === 0) return null;

  const grouped: Record<string, CandidateAttribute[]> = {};
  for (const c of candidates) {
    (grouped[c.dim] ??= []).push(c);
  }
  for (const k of Object.keys(grouped)) {
    grouped[k].sort((a, b) => b.probability - a.probability);
  }

  const presentDims = Object.keys(grouped);
  const orderedDims = [
    ...DIM_ORDER.filter((d) => presentDims.includes(d)),
    ...presentDims.filter((d) => !DIM_ORDER.includes(d)).sort(),
  ];

  return (
    <div className="rj-inline-card__attrs">
      {orderedDims.map((dim) => (
        <div key={dim} className="rj-inline-card__attr-row">
          <span className="rj-inline-card__attr-label">{DIM_LABELS[dim] ?? dim}</span>
          <div className="rj-inline-card__attr-pills">
            {grouped[dim]!.map((c) => {
              const isOn = selected[dim] === c.value;
              const pct = Math.round(c.probability * 100);
              return (
                <button
                  key={c.value}
                  type="button"
                  className={`rj-inline-card__pill ${isOn ? "rj-inline-card__pill--on" : ""}`}
                  onClick={() => onTogglePill(dim, c.value)}
                >
                  <span className="rj-inline-card__pill-value">{c.value.replace(/_/g, " ")}</span>
                  <span className="rj-inline-card__pill-prob">{pct}%</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
